<svelte:options runes={true} />

<script lang="ts">
  import type { CommandManager } from '@commands';
  import { CommandId } from '@commands';
  import type { Doc } from '@doc';
  import { LayoutOrientation, nameForOrientation } from '@doc';
  import { localeManager } from '@reactive';
  import type { MenuSelectEventDetail } from '@shoelace-style/shoelace/dist/components/menu/menu.component.js';
  import { StringId } from '@strings';
  import {
    APP_ICON_BOTTOM_TO_TOP,
    APP_ICON_LEFT_TO_RIGHT,
    APP_ICON_RIGHT_TO_LEFT,
    APP_ICON_TOP_TO_BOTTOM,
    APP_ICON_UNCONSTRAINED,
    SlFontSize,
    type AppIcon
  } from '@ui';
  import { reactTo } from '@utils';
  import Icon from './Icon.svelte';

  let {
    doc = $bindable(),
    commandManager
  }: {
    doc: Doc;
    commandManager: CommandManager;
  } = $props();

  function handleSelect(event: CustomEvent<MenuSelectEventDetail>) {
    const value = event.detail.item.value;
    let command = value as CommandId;
    switch (value as LayoutOrientation) {
      case LayoutOrientation.UNCONSTRAINED:
        command = CommandId.LAYOUT_UNCONSTRAINED;
        break;
      case LayoutOrientation.LEFT_TO_RIGHT:
        command = CommandId.LAYOUT_LEFT_TO_RIGHT;
        break;
      case LayoutOrientation.TOP_TO_BOTTOM:
        command = CommandId.LAYOUT_TOP_TO_BOTTOM;
        break;
      case LayoutOrientation.RIGHT_TO_LEFT:
        command = CommandId.LAYOUT_RIGHT_TO_LEFT;
        break;
      case LayoutOrientation.BOTTOM_TO_TOP:
        command = CommandId.LAYOUT_BOTTOM_TO_TOP;
        break;
    }
    commandManager.dispatch(command);
  }

  let checkedState = $derived.by(() => {
    const docOrientation = doc.orientation;
    // An Object with a boolean for each orientation
    let result: Record<string, boolean> = {};
    for (const orientation of Object.values(LayoutOrientation)) {
      result[orientation] = orientation === docOrientation;
    }

    return result;
  });

  let items = $derived.by(() => {
    reactTo(localeManager.localeId);
    // An Object with the name of each orientation
    let result: Record<string, string> = {};
    for (const orientation of Object.values(LayoutOrientation)) {
      result[orientation] = nameForOrientation(orientation);
    }

    return result;
  });

  let icons = $derived.by(() => {
    // An Object with the icon for each orientation
    let result: Record<string, AppIcon> = {};
    result[LayoutOrientation.LEFT_TO_RIGHT] = APP_ICON_LEFT_TO_RIGHT;
    result[LayoutOrientation.TOP_TO_BOTTOM] = APP_ICON_TOP_TO_BOTTOM;
    result[LayoutOrientation.RIGHT_TO_LEFT] = APP_ICON_RIGHT_TO_LEFT;
    result[LayoutOrientation.BOTTOM_TO_TOP] = APP_ICON_BOTTOM_TO_TOP;
    result[LayoutOrientation.UNCONSTRAINED] = APP_ICON_UNCONSTRAINED;
    return result;
  });
</script>

<style>
  .truncate-button::part(label) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
</style>

<div style="max-width: 100%;" class="dropdown-selection">
  <sl-dropdown style="max-width: 100%;">
    <sl-button
      slot="trigger"
      style="max-width: 100%;"
      class="truncate-button"
      caret
      size="small"
    >
      <div slot="prefix">
        {#if doc.orientation === LayoutOrientation.UNCONSTRAINED}
          <Icon icon={APP_ICON_UNCONSTRAINED} inline size={SlFontSize.LG} />
        {:else if doc.orientation === LayoutOrientation.LEFT_TO_RIGHT}
          <Icon icon={APP_ICON_LEFT_TO_RIGHT} inline size={SlFontSize.LG} />
        {:else if doc.orientation === LayoutOrientation.TOP_TO_BOTTOM}
          <Icon icon={APP_ICON_TOP_TO_BOTTOM} inline size={SlFontSize.LG} />
        {:else if doc.orientation === LayoutOrientation.RIGHT_TO_LEFT}
          <Icon icon={APP_ICON_RIGHT_TO_LEFT} inline size={SlFontSize.LG} />
        {:else if doc.orientation === LayoutOrientation.BOTTOM_TO_TOP}
          <Icon icon={APP_ICON_BOTTOM_TO_TOP} inline size={SlFontSize.LG} />
        {/if}
      </div>
      {nameForOrientation(doc.orientation)}
    </sl-button>
    <sl-menu onsl-select={handleSelect} value={doc.orientation}>
      {#each Object.keys(items) as item}
        <sl-menu-item checked={checkedState[item]} type="checkbox" value={item}>
          <div
            style="display: flex; flex-direction: row; align-items: center; gap: 0.5rem"
          >
            <Icon icon={icons[item]} inline size={SlFontSize.LG} />
            <div>{items[item]}</div>
          </div>
        </sl-menu-item>
      {/each}

      <sl-divider></sl-divider>

      <sl-menu-item
        checked={doc.alignRoots}
        disabled={doc.orientation === LayoutOrientation.UNCONSTRAINED}
        type="checkbox"
        value={CommandId.TOGGLE_ALIGN_ROOTS}
      >
        <div>{localeManager.localizedString(StringId.TOGGLE_ALIGN_ROOTS)}</div>
      </sl-menu-item>

      <sl-menu-item
        checked={doc.alignLeaves}
        disabled={doc.orientation === LayoutOrientation.UNCONSTRAINED}
        type="checkbox"
        value={CommandId.TOGGLE_ALIGN_LEAVES}
      >
        <div>{localeManager.localizedString(StringId.TOGGLE_ALIGN_LEAVES)}</div>
      </sl-menu-item>

      <sl-divider></sl-divider>

      <sl-menu-item
        checked={doc.bundleEdges}
        type="checkbox"
        value={CommandId.TOGGLE_BUNDLED_ROUTING}
      >
        <div style="display: flex; flex-direction: row; align-items: center; gap: 0.5rem">
          <div>{localeManager.localizedString(StringId.TOGGLE_BUNDLED_ROUTING)}</div>
        </div>
      </sl-menu-item>
    </sl-menu>
  </sl-dropdown>
</div>
